import { Component, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CopyRialButton } from '../../shared/components/copy-rial-button/copy-rial-button';
import { MAX_PROOFS, PROOF_ACCEPT, addProofFiles } from '../proof-files';
import { PaymentDetails } from '../payment.type';
import { PaymentSourceFields } from '../payment-source-fields/payment-source-fields';

export interface SettlementProofDialogData {
  /** Shown in the dialog title. */
  title: string;
  /** Shown as the submit button label. */
  submitLabel: string;
  /** Optional summary line, e.g. "3 item(s)". */
  summary?: string;
  /** The amount being paid; shown prominently with a Copy Rial button for the transfer. */
  amount?: number;
  /** How many proofs the settlement already has, so the picker stops at the cap. */
  existingProofs?: number;
  /** Marking paid: ask which account the money left from (required). */
  askPayment?: boolean;
}

export interface SettlementProofDialogResult {
  note: string;
  proofFiles: File[];
  /** Set when askPayment was. */
  payment?: PaymentDetails;
}

/**
 * Collects optional payment-proof images and a note before a settlement is
 * recorded or marked paid — a bare button click can't carry files, so this is
 * the point where they get attached. Proofs are optional here: they can also
 * be added or removed later from the settlement's Proofs dialog.
 */
@Component({
  selector: 'app-settlement-proof-dialog',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTooltipModule,
    CopyRialButton,
    PaymentSourceFields,
  ],
  templateUrl: './settlement-proof-dialog.html',
  styleUrl: './settlement-proof-dialog.scss',
})
export class SettlementProofDialog implements OnDestroy {
  private fb = inject(FormBuilder);
  private dialogRef = inject(MatDialogRef<SettlementProofDialog>);
  readonly data = inject<SettlementProofDialogData>(MAT_DIALOG_DATA);
  readonly accept = PROOF_ACCEPT;
  readonly room = Math.max(0, MAX_PROOFS - (this.data.existingProofs ?? 0));

  form: FormGroup = this.fb.group({ note: [''] });
  payment: PaymentDetails = { source_account_id: '', bank_fee: 0, transaction_ref: '' };

  get canSubmit(): boolean {
    return !this.data.askPayment || !!this.payment.source_account_id;
  }
  proofs: { file: File; url: string }[] = [];
  fileError = '';

  onFilesSelected(event: Event): void {
    const current = this.proofs.map(p => p.file);
    const { files, error } = addProofFiles(event.target as HTMLInputElement, current, this.room);
    this.fileError = error;
    for (const file of files.slice(current.length)) {
      this.proofs.push({ file, url: URL.createObjectURL(file) });
    }
  }

  removeFile(index: number): void {
    URL.revokeObjectURL(this.proofs[index].url);
    this.proofs.splice(index, 1);
    this.fileError = '';
  }

  onSubmit(): void {
    if (!this.canSubmit) return;
    const result: SettlementProofDialogResult = {
      note: this.form.value.note ?? '',
      proofFiles: this.proofs.map(p => p.file),
      payment: this.data.askPayment ? this.payment : undefined,
    };
    this.dialogRef.close(result);
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  ngOnDestroy(): void {
    for (const p of this.proofs) URL.revokeObjectURL(p.url);
  }
}
